import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { journey } from "@/mocks/home";

export default function Journey() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>(".journey-card");
      cards.forEach((card, i) => {
        gsap.from(card, {
          y: 80,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
          },
          delay: i * 0.05,
        });
      });

      // Progress line
      gsap.from(".journey-line", {
        scaleY: 0,
        transformOrigin: "top",
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top 60%",
          end: "bottom 60%",
          scrub: 0.6,
        },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-forest text-bone py-16 md:py-20 px-6 md:px-10 relative overflow-hidden">
      {/* Decoration */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(245,241,234,0.6) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 md:mb-14 gap-6">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-clay mb-4">
              ✦ Soil to silhouette
            </p>
            <h3 className="font-display text-5xl md:text-7xl tracking-tightest font-light leading-[0.95]">
              Every garment, <br />
              <span className="font-display text-clay">told in four hands.</span>
            </h3>
          </div>
          <p className="text-bone/70 max-w-sm text-sm md:text-base leading-relaxed">
            Scroll the journey of a single linen dress — from the flax field in Trás-os-Montes to the finished piece that arrives at your door.
          </p>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-bone/15 hidden md:block">
            <div className="journey-line absolute inset-0 bg-clay"></div>
          </div>

          <div className="space-y-12 md:space-y-16">
            {journey.map((j, i) => (
              <div
                key={j.step}
                className={`journey-card relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:px-12">
                  <span className="font-mono text-xs tracking-[0.3em] text-clay">STEP {j.step}</span>
                  <h4 className="mt-4 font-display text-5xl md:text-7xl tracking-tightest font-light text-bone leading-none">
                    {j.title}
                  </h4>
                  <p className="mt-6 text-bone/70 text-base md:text-lg leading-relaxed max-w-md">
                    {j.body}
                  </p>
                </div>
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                  <img
                    src={j.image}
                    alt={j.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-forest/80 backdrop-blur text-[10px] font-mono uppercase tracking-[0.2em] text-bone border border-bone/20">
                    {j.title}
                  </div>
                </div>
                {/* Dot on the line */}
                <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-clay z-10 ring-4 ring-forest"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}